/**
 * FreshCards - Main Entry Point
 * Phase 0: Foundations
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { MantineProvider, createTheme } from '@mantine/core';
import App from './App';
import './index.css';
import '@mantine/core/styles.css';
import '@blocknote/core/style.css';
import '@blocknote/mantine/style.css';

const root = ReactDOM.createRoot(document.getElementById('root')!);

root.render(
  <React.StrictMode>
    <MantineProvider
      defaultColorScheme="light"
      theme={createTheme({
        // Primary Colors - FreshVibe Design System
        primaryColor: 'blue',
        colors: {
          // FreshVibe Primary Palette
          blue: ['#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#1e3a8a', '#172554'],
          
          // FreshVibe Secondary Palette
          gray: ['#f3f4f6', '#e5e7eb', '#d1d5db', '#9ca3af', '#6b7280', '#4b5563', '#374151', '#1f2937', '#111827', '#000000'],
          
          // FreshVibe Success Palette
          teal: ['#ecfdf5', '#d1fae5', '#a7f3d0', '#6ee7b7', '#34d399', '#10b981', '#059669', '#047857', '#065f46', '#064e3b'],
          
          // FreshVibe Warning Palette
          amber: ['#fffbeb', '#fef3c7', '#fde68a', '#fcd34d', '#fbbf24', '#f59e0b', '#d97706', '#b45309', '#92400e', '#78350f'],
          
          // FreshVibe Error Palette
          red: ['#fef2f2', '#fee2e2', '#fecaca', '#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d'],
          
          // FreshVibe Info Palette
          indigo: ['#eef2ff', '#e0e7ff', '#c7d2fe', '#a5b4fc', '#818cf8', '#6366f1', '#4f46e5', '#4338ca', '#3730a3', '#312e81'],
          
          // FreshVibe Accent Palette
          purple: ['#f3e8ff', '#e9d5ff', '#d8b4fe', '#c084fc', '#a855f7', '#9333ea', '#7c3aed', '#6d28d9', '#5b21b6', '#4c1d95'],
          
          // FreshVibe Neutral Palette
          slate: ['#f8fafc', '#f1f5f9', '#e2e8f0', '#cbd5e1', '#94a3b8', '#64748b', '#475569', '#334155', '#1e293b', '#0f172a']
        },

        // Typography - FreshVibe Design System
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        fontFamilyMonospace: 'SF Mono, Monaco, Inconsolata, "Roboto Mono", "Source Code Pro", Menlo, Consolas, "Courier New", monospace',
        
        // Spacing - FreshVibe Design System (converting to Mantine scale)
        spacing: {
          xs: '2px',
          sm: '4px',
          md: '8px',
          lg: '16px',
          xl: '24px',
          '2xl': '32px',
          '3xl': '48px',
          '4xl': '64px',
          '5xl': '80px'
        },

        // Border Radius - FreshVibe Design System
        radius: {
          xs: '3px',
          sm: '6px',
          md: '8px',
          lg: '12px',
          xl: '16px',
          '2xl': '24px',
          full: '9999px'
        },

        // Shadows - FreshVibe Design System
        shadows: {
          xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
          none: 'none'
        },

        // Component Overrides
        components: {
          // Paper component
          Paper: {
            styles: {
              root: {
                background: 'var(--fc-bg-primary)',
                borderRadius: 'var(--fc-radius-lg)',
                border: '1px solid var(--fc-border-primary)',
                transition: 'all 0.2s ease'
              }
            }
          },
          
          // Card component
          Card: {
            styles: {
              root: {
                background: 'var(--fc-bg-primary)',
                border: '1px solid var(--fc-border-primary)',
                borderRadius: 'var(--fc-radius-lg)',
                transition: 'all 0.2s ease'
              },
              section: {
                padding: 'var(--fc-space-lg)'
              }
            }
          },
          
          // Button component
          Button: {
            styles: {
              root: {
                transition: 'all 0.2s ease',
                borderRadius: 'var(--fc-radius-md)'
              },
              primary: {
                background: 'var(--fc-accent-primary)',
                color: 'white',
                '&:hover': {
                  background: 'var(--fc-accent-hover)'
                }
              },
              secondary: {
                background: 'var(--fc-bg-tertiary)',
                color: 'var(--fc-text-primary)',
                border: `1px solid var(--fc-border-primary)`,
                '&:hover': {
                  background: 'var(--fc-bg-hover)'
                }
              }
            }
          },
          
          // Input component
          Input: {
            styles: {
              input: {
                border: `1px solid var(--fc-border-primary)`,
                borderRadius: 'var(--fc-radius-md)',
                background: 'var(--fc-bg-primary)',
                color: 'var(--fc-text-primary)',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                '&:focus': {
                  borderColor: 'var(--fc-accent-primary)',
                  boxShadow: '0 0 0 3px var(--fc-accent-muted)'
                }
              }
            }
          },
          
          // List component
          List: {
            styles: {
              item: {
                padding: 'var(--fc-space-md)',
                borderRadius: 'var(--fc-radius-md)',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: 'var(--fc-bg-hover)'
                }
              }
            }
          }
        }
      })}
    >
      <App />
    </MantineProvider>
  </React.StrictMode>
);

export default App;
